import { RouterRootState } from "connected-react-router";
import React, { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router";
import { toast } from "react-toastify";
import { useDialog } from "../../../components/dialog/DialogContext";
import ItemContentTabTwo from "../../../components/item-content-two/item-content-tab_two";
import { Topic } from "../../../components/item-content-two/Topic";
import SimpleItemDetails from "../../../components/item-content/SimpleItemDetails";
import { EmailTemplate } from "../../../components/notifications/email-template-step";
import EventBasedDetails from "../../../components/notifications/event-based/event-based-details";
import EventDisplay from "../../../components/notifications/event-based/events-display";
import { NotificationCard } from "../../../components/notifications/notification-card";
import { NotificationDetails } from "../../../components/notifications/notificationDetails";
import Recipients from "../../../components/notifications/recipients";
import Schedule from "../../../components/notifications/schedule/schedule";
import { DAILY_OPTION, EVENT_BASED_NOTIFICATION, FILTER_TIME_ZONE, MONTHLY_OPTION, MSG_BODY, MSG_DISCLAIMER, MSG_SUBJECT, PATTERN, PATTERN_DAY_OF_MONTH, PATTERN_DAY_OF_WEEK, PATTERN_DAY_TYPE, PATTERN_EVERY_DAY, PATTERN_EVERY_MONTH, PATTERN_EVERY_WEEK, PATTERN_EVERY_YEAR, PATTERN_MONTH_OF_YEAR, RANGE_END_DATE, RANGE_OCCURRENCE, RANGE_START_DATE, RECURRENCE, START_TIME, YEARLY_OPTION } from "../../../constants/notification-key";
import { useLocale } from "../../../contexts/localeContext";
import { TopicContextProvider } from "../../../contexts/topic/TopicContextProvider";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import { BreadcrumbLink } from "../../../models/BreadcrumbLink";
import { ContentWrapper } from "../../../models/ContentWrapper";
import { MultiSelectOption } from "../../../models/MultiSelectOption";
import { NotificationTabs } from "../../../models/NotificationWizardSteps";
import { ScheduleNotification } from "../../../models/ScheduleNotification";
import { SettingData } from "../../../models/SettingData";
import { TabSummary } from "../../../models/TabSummary";
import { editNotification, getNotificationById, getNotificationNamesWithPagination } from "../../../services/notificationService";
import { convertDateToString, getFormattedTime, getTime } from "../../../services/utilities/dateFormatting";
import { getErrorMessage, showLoadItemError } from "../../../services/utilities/ServiceError";
import NotificationAuditTab from "../../../components/notifications/audit-tab";

const getIdFromPath = (path: string) => {
    const splitPath = path.split("/");
    return splitPath[splitPath.length - 1];
};

const initialItem: ScheduleNotification = {
    vruleActionId: 0,
    name: "",
    id: 0,
    time: "",
    filterEvents: [],
    daysOfTheWeek: [],
    timeZone: "",
    timeZoneId: "",
    pattern: PATTERN.DAILY,
    startDate: "",
    occurrences: "1",
    type: "",
    toRecipients: [],
    ccRecipients: [],
    emailAccount: "",
    emailAccountId: "",
    subject: "",
    body: "",
    disclaimer: "",
    report: "",
    reportId: "",
    recurrenceOption: RECURRENCE.NO_END_DATE,
    typeId: "",
    dailyOption: DAILY_OPTION.EVERY,
    settingDatasRef: [],
    enabled: 0,
    lastTriggered: ""
};

const allItemsPath = "/access-control/notifications";
const itemPath = "/access-control/notifications/notification/";

const NotificationPage: FC = () => {
    const { translate } = useLocale();
    const [currentItem, setCurrentItem] = useState<ScheduleNotification>(initialItem);
    const history = useHistory();
    const pageId = useSelector((state: RouterRootState) =>
        getIdFromPath(state.router.location.pathname),
    );
    const [itemId, setItemId] = useState(pageId);
    const [breadcrumb, setBreadcrumb] = useState<BreadcrumbLink[]>([]);
    const [busy, setBusy] = useState(false);
    const dialog = useDialog();
    let settingDataToAdd: SettingData[] = []; //Need to move this type
    let settingDataToEdit: SettingData[] = []; //Need to move this type
    let settingDataToRemove: string[] = [];
    const [tabs, setTabs] = useState<TabSummary[]>([]);
    const [selectedItem, setSelectedItem] = useState<MultiSelectOption | undefined>(undefined);

    const scNotificationsTabs:NotificationTabs[] = [
        {
            label: translate.byKey(TranslationKeys.details),
            badge: 0,
            showInitial: true,
            isActive: true,
        },
        {
            label: translate.byKey(TranslationKeys.recipient),
            badge: 0,
            showInitial: true,
            isActive: true,
        },
        {
            label: translate.byKey(TranslationKeys.schedule),
            badge: 0,
            showInitial: true,
            isActive: true,
        },
        {
            label: translate.byKey(TranslationKeys.emailTemplate),
            badge: 0,
            showInitial: true,
            isActive: true,
        },
        {
            label: translate.byKey(TranslationKeys.audit),
            badge: 0,
            showInitial: true,
            isActive: true,
        }
    ];

    useEffect(() => {
        const updatedTabs = updateTabBadgeTotal(0, currentItem.filterEvents?.length, updateTabBadgeTotal(1, currentItem.filterIds?.length, tabs))
        setTabs(updatedTabs);
    }, [currentItem.filterEvents])

    const evNotificationsTabs:NotificationTabs[] = [
        {
            label: translate.byKey(TranslationKeys.events),
            badge:  0,
            showInitial: true,
            isActive: true,
        },
        {
            label: translate.byKey(TranslationKeys.filterBy),
            badge: 0,
            showInitial: true,
            isActive: true,
        },
        {
            label: translate.byKey(TranslationKeys.recipient),
            badge: 0,
            showInitial: true,
            isActive: true,
        },
        {
            label: translate.byKey(TranslationKeys.emailTemplate),
            badge: 0,
            showInitial: true,
            isActive: true,
        },
        {
            label: translate.byKey(TranslationKeys.audit),
            badge: 0,
            showInitial: true,
            isActive: true,
        }
    ]

    useEffect(() => {
        setItemId(pageId);
    }, [pageId]);

    useEffect(() => {
        reloadPage();
    }, [itemId]);

    const reloadPage = async () => {
        try {
            let notification:any;
            if (itemId) {
                notification = await getNotificationById(itemId);
                
                setTabs(notification.typeId == EVENT_BASED_NOTIFICATION ? evNotificationsTabs : scNotificationsTabs);
                setSelectedItem({label: notification.name, value: notification.id.toString()})
                setCurrentItem(notification);
            } else {
                setCurrentItem(initialItem);
                setTabs([]);
            }

            setBreadcrumb([
                {
                    value: "/access-control/notifications",
                    displayName: translate.byKey(TranslationKeys.notifications),
                    disableClick: true,
                },
                {
                    value: allItemsPath,
                    displayName: translate.byKey(TranslationKeys.notification),
                },
                {
                    value: itemPath + `${itemId}`,
                    displayName: `${notification?.name ?? ""}`,
                },
            ]);
        } catch (error) {
            showLoadItemError(error, dialog, translate, () => {
                history.push(allItemsPath);
            });
        }
    };

    const topic: Topic = {
        accessor: "name",
        allItemsPath,
        itemPath,
        title: translate.byKey(TranslationKeys.notifications),
    };
    
    const scheduleNotificationContent: ContentWrapper[] = [
        {
            content: <NotificationDetails />,
        },
        {
            content: (
                <Recipients ccRecipients={currentItem.ccRecipients} toRecipients={currentItem.toRecipients} reloadPage={reloadPage} />
            ),
        },
        {
            content: <Schedule />
        },
        {
            content: (
                <div style={{ width: "60%" }}>
                    <EmailTemplate />
                </div>
            ),
        },
        {
            content: <NotificationAuditTab notificationId={currentItem.id} />,
        }
    ];

    const eventBasedContentList: ContentWrapper[] = [
        {
            content: <EventDisplay reloadPage={reloadPage} currentItem={currentItem} id={pageId} />,
        },
        {
            content: <EventBasedDetails reloadPage={reloadPage}  />,
        },
        {
            content: (
                <Recipients ccRecipients={currentItem.ccRecipients} toRecipients={currentItem.toRecipients} reloadPage={reloadPage} />
            ),
        },
        {
            content: (
                <div style={{ width: "60%" }}>
                    <EmailTemplate />
                </div>
            ),
        },
        {
            content: <NotificationAuditTab notificationId={currentItem.id} />,
        }
    ]

    const updateTabBadgeTotal = (tabIndex, totalCount, currentTabs) => {
        const newTabs = [...currentTabs];
        if (newTabs[tabIndex] !== undefined) {
            newTabs[tabIndex].badge = totalCount;
        }
        return newTabs;
    };

    const getTabContent = (index?: number) => {
        const selectedTabs: ContentWrapper[] = +currentItem.typeId == 2 ? eventBasedContentList : scheduleNotificationContent;

        if (!tabs[index ?? 0]) {
            return selectedTabs[0].content;
        } else if (itemId && tabs[index ?? 0].isActive) {
            const selectedTabContent = selectedTabs[index ?? 0];
            return selectedTabContent.content;
        }
        return selectedTabs[0].content;
    };

    const updateNotification = (values) => {
        setBusy(true)
        settingDataToAdd = []; settingDataToEdit = []; settingDataToRemove = [];
        let name: string | undefined, emailAccountId: string | undefined,reportIdToSave:string | undefined;
        const fields: { accessor: string, elementSysname: string }[] = [
            { accessor: "dayOfMonth", elementSysname: PATTERN_DAY_OF_MONTH },
            { accessor: "occurrence", elementSysname: PATTERN_DAY_TYPE },
            { accessor: "monthOfTheYear", elementSysname: PATTERN_MONTH_OF_YEAR },
            { accessor: "startDate", elementSysname: RANGE_START_DATE },
            { accessor: "time", elementSysname: START_TIME },
            { accessor: "rangeOccurrence", elementSysname: RANGE_OCCURRENCE },
            { accessor: "subject", elementSysname: MSG_SUBJECT },
            { accessor: "body", elementSysname: MSG_BODY },
            { accessor: "disclaimer", elementSysname: MSG_DISCLAIMER },
        ];

        // If the recurrence option is set to end by, we need to add the end date to the fields
        // If not, we can ignore it
        if (values.recurrenceOption === RECURRENCE.END_BY) {
            fields.push({ accessor: "endDate", elementSysname: RANGE_END_DATE });
        }

        let updatedValues = {
            ...values,
            daysOfTheWeek: values.dailyOption && values.dailyOption === DAILY_OPTION.EVERY_WEEKDAY ?
                ["MON", "TUE", "WED", "THU", "FRI"] :
                values.daysOfTheWeek
        }
        
        const changedValues: string[] = findChangedAttributes(getNotificationTrackable(updatedValues), getNotificationTrackable(currentItem));

        //We should always do this updated after we have found change values otherwise it we see these and change which is not correct
        updatedValues = {
            ...updatedValues,
            endDate: convertDateToString(values.endDate),
            startDate: convertDateToString(values.startDate),
            time: getFormattedTime(getTime(values.time))
        };
       
        changedValues.forEach((accessor) => {
            if (accessor === "timeZoneId") {
                prepareTimeZone(updatedValues.timeZoneId, updatedValues.settingDatasRef);
            }
            else if (accessor === "daysOfTheWeek") {
                prepareDayOfTheWeek(updatedValues);
            }
            else if (accessor === "occurrences") {
                prepareOccurrences(values);
            }
            else if (accessor === "name") {
                name = values.name;
            }
            else if (accessor === "emailAccountId") {
                emailAccountId = values.emailAccountId
            }
            else if(accessor === 'recurrenceOption'){
                processRangeReccurrences(values)
            }else if(accessor === 'reportId'){
                reportIdToSave = values.reportId;
            }
            else {
                const field = fields.find(field => field.accessor === accessor);
                if (field) {
                    buildSettingData(field.accessor, field.elementSysname, updatedValues)
                }
            }
        });

        if(currentItem.occurrences === updatedValues.occurrences && currentItem.pattern !== updatedValues.pattern) {
            prepareOccurrences(values);
        }

        if(currentItem.occurrence === updatedValues.occurrence && currentItem.pattern  !== updatedValues.pattern){
            removeOccurence(updatedValues)
        }
        
        /*
            After everything is done we need to find settingDatas that need to be removed.
            Why you may ask, this so that we don't keep data that is not not longer relevant.
            Use case which from daily every to daily every weekday. Here we need to delete occurrences
            since it's not used by daily every weekday
        */
        cleanSchedule(currentItem.settingDatasRef, getRelevantElementSysname(values))

        let enabledValue = values.enabled ? 1 : 0;

        //Name should only be passed if it was changed
        editNotification(settingDataToRemove, settingDataToAdd, settingDataToEdit, currentItem.id, currentItem.vruleActionId, name, emailAccountId, values.reportId, enabledValue)
            .then((response) => {
                toast.success(translate.byKey(TranslationKeys.notificationUpdated));
                reloadPage();
            }).catch((error) => {
                dialog.pushError({
                    title: translate.byKey(TranslationKeys.error),
                    message: getErrorMessage(error),
                });
            }).finally(() => {
                setBusy(false);
            });
    }

    /**
     * Build time seperate because it have a unique relationship can be improved 
     * 
     * @param timeZoneId 
     * @param settingDatasRef 
     */
    const prepareTimeZone = (timeZoneId: string, settingDatasRef) => {
        const settingData = settingDatasRef.find(settingDataRef => settingDataRef.elementSysname === FILTER_TIME_ZONE)
        if (settingData) {
            settingDataToEdit.push({
                id: settingData.id,
                sdValstring: timeZoneId,
                relationship: [
                    {
                        id: timeZoneId,
                        type: "timeZone"
                    }
                ]
            })
        }
    }

    /**
    *
    * This is a back-bone of how we build setting data this will check if the seting data exist 
    * if it does that we are mark it as to be edited otherwise as to be added(new)
    *
    * @param accessor 
    * @param elementSysname 
    * @param elementId 
    * @param values 
    * @returns 
    */
    const buildSettingData = (accessor: string, elementSysname: string, values) => {
        const settingData = values.settingDatasRef.find(settingDataRef => settingDataRef.elementSysname === elementSysname)
        if (settingData) {
            settingDataToEdit.push({
                id: settingData.id,
                sdValstring: values[accessor]
            })

            return;
        }

        settingDataToAdd.push({
            sdValstring: values[accessor],
            eSysnameLookup: elementSysname,
            relationship: [
                {
                    id: values.vruleTriggerScheduleId,
                    type: "ruleTriggerGroup",
                    relationType:"vruleTriggerGroup"
                }
            ]
        });
    }

    const processRangeReccurrences = (values) => {
        const filteredSettingDataRefs = values.settingDatasRef.filter(
            (item) => item.elementSysname === RANGE_OCCURRENCE || item.elementSysname === RANGE_END_DATE
          );
        if(values.recurrenceOption === RECURRENCE.NO_END_DATE || values.recurrenceOption === RECURRENCE.END_BY || values.recurrenceOption === RECURRENCE.END_AFTER){
            filteredSettingDataRefs.forEach((item) => {
                settingDataToRemove.push(item.id)
            })  
        }
    }

    /**
     * Days of the week are different because can be either a string or an array
     * with that we need to handle the cases differently. 
     *
     * @param values 
     * @returns 
     */

    const prepareDayOfTheWeek = (values) => {
        if (Array.isArray(values.daysOfTheWeek)) {
             //If it is an array
            const newDaysOfTheWeek = values.daysOfTheWeek.filter((day: string) => !currentItem.daysOfTheWeek?.includes(day));

            newDaysOfTheWeek.forEach((day: string) => {
                settingDataToAdd.push({
                    sdValstring: day,
                    eSysnameLookup: PATTERN_DAY_OF_WEEK,
                    relationship: [
                        {
                            id: values.vruleTriggerScheduleId,
                            type: "ruleTriggerGroup",
                            relationType:"vruleTriggerGroup"
                        }
                    ]
                })
            });
        

        }else if(typeof values.daysOfTheWeek === "string"){
            settingDataToAdd.push({
                sdValstring: values.daysOfTheWeek,
                eSysnameLookup: PATTERN_DAY_OF_WEEK,
                relationship: [
                    {
                        id: values.vruleTriggerScheduleId,
                        type: "ruleTriggerGroup",
                        relationType:"vruleTriggerGroup"
                    }
                ]
            })
        }
        
        //Delete if there's anything to delete
        const oldDaysOfTheWeek: any = currentItem.daysOfTheWeek;
      
        if(oldDaysOfTheWeek){
            const toRemove = compareAndRemove(oldDaysOfTheWeek, values.daysOfTheWeek);
            toRemove.forEach((day: string) => {
                const settingData = values.settingDatasRef.find(settingData => settingData.sdValstring === day);
                if (settingData) {
                    settingDataToRemove.push(settingData.id);
                }
            })
        }
    }

    const removeOccurence = (values) => {
        const settingData = values.settingDatasRef.find(settingData => settingData.sdValstring === values.occurrence);
        if(settingData){
            settingDataToRemove.push(settingData.id)
        }
    }

    const prepareOccurrences = (values: any) => {
        const patterns: { key: string, elementSysname: string }[] = [
            { key: PATTERN.DAILY, elementSysname: PATTERN_EVERY_DAY },
            { key: PATTERN.WEEKLY, elementSysname: PATTERN_EVERY_WEEK },
            { key: PATTERN.MONTHLY, elementSysname: PATTERN_EVERY_MONTH },
            { key: PATTERN.YEARLY, elementSysname: PATTERN_EVERY_YEAR }
        ];
        const patternElementSysname = patterns.find(pattern => pattern.key === values.pattern)?.elementSysname ?? '';
        buildSettingData("occurrences", patternElementSysname, values)
    }

    const getRelevantElementSysname = (values: any): string[] => {
        let data: string[] = [];

        if (values.pattern === PATTERN.DAILY) {
            data = values.dailyOption === DAILY_OPTION.EVERY_WEEKDAY ?
                [PATTERN_EVERY_DAY, PATTERN_DAY_OF_WEEK,RANGE_END_DATE,RANGE_OCCURRENCE] :
                [PATTERN_EVERY_DAY,RANGE_END_DATE,RANGE_OCCURRENCE];
        }
        else if (values.pattern === PATTERN.WEEKLY) {
            data = [PATTERN_EVERY_WEEK, PATTERN_DAY_OF_WEEK,RANGE_END_DATE,RANGE_OCCURRENCE];
        }
        else if (values.pattern === PATTERN.MONTHLY) {
            data = values.monthlyOption === MONTHLY_OPTION.DAY ?
                [PATTERN_EVERY_MONTH, PATTERN_DAY_OF_MONTH,RANGE_END_DATE,RANGE_OCCURRENCE] :
                [PATTERN_EVERY_MONTH, PATTERN_DAY_OF_MONTH, PATTERN_DAY_OF_WEEK,RANGE_END_DATE,RANGE_OCCURRENCE];
        }
        else if (values.pattern === PATTERN.YEARLY) {
            data = values.yearlyOption === YEARLY_OPTION.ON ?
                [PATTERN_EVERY_YEAR, PATTERN_DAY_OF_MONTH, PATTERN_MONTH_OF_YEAR,RANGE_END_DATE,RANGE_OCCURRENCE] :
                [PATTERN_EVERY_YEAR, PATTERN_DAY_OF_MONTH, PATTERN_MONTH_OF_YEAR, PATTERN_DAY_OF_WEEK,RANGE_END_DATE,RANGE_OCCURRENCE];
        }

        return data;
    }

    function compareAndRemove(value1: string | string[], value2: string | string[]): string[] {
        let arr1: string[];
        let arr2: string[];
    
        if (typeof value1 === 'string') {
            arr1 = [value1];
        } else {
            arr1 = value1;
        }
    
        if (typeof value2 === 'string') {
            arr2 = [value2];
        } else {
            arr2 = value2;
        }

        if(arr1 === undefined){
            return []
        }
    
        return arr1.filter(item => !arr2.includes(item));
    }
    
    const cleanSchedule = (settingDataRef, relevantField: string[]) => {
        const scheduleElementSysname: string[] = [
            PATTERN_DAY_OF_MONTH,
            PATTERN_DAY_OF_WEEK,
            PATTERN_MONTH_OF_YEAR,
            PATTERN_EVERY_DAY,
            PATTERN_EVERY_MONTH,
            PATTERN_EVERY_WEEK,
            PATTERN_EVERY_YEAR,
            RANGE_OCCURRENCE,
            RANGE_END_DATE
        ];

        settingDataRef.forEach(settingData => {
            if (!relevantField.includes(settingData.elementSysname) && scheduleElementSysname.includes(settingData.elementSysname)) {
                settingDataToRemove.push(settingData.id)
            }
        });
    }

    //TODO - this might need to move to a utility file for future use
    const findChangedAttributes = (updatedValues, initialValues): string[] => {
        const changedAttributes: string[] = [];

        for (const key in updatedValues) {
            if (updatedValues.hasOwnProperty(key) && initialValues.hasOwnProperty(key)) {
                if (updatedValues[key] !== initialValues[key]) {
                    changedAttributes.push(key);
                }
            }
        }

        return changedAttributes;
    }

    const getNotificationTrackable = (values: any) => {
        return {
            dayOfMonth: values.dayOfMonth,
            occurrence: values.occurrence,
            monthOfTheYear: values.monthOfTheYear,
            startDate: values.startDate,
            time: values.time,
            endDate: values.endDate,
            rangeOccurrence: values.rangeOccurrence,
            subject: values.subject,
            body: values.body,
            disclaimer: values.disclaimer,
            daysOfTheWeek: values.daysOfTheWeek,
            name: values.name,
            occurrences: values.occurrences,
            emailAccountId:values.emailAccountId,
            recurrenceOption: values.recurrenceOption,
            reportId: values.reportId,
            timeZoneId:values.timeZoneId
        }
    }

    return (
        <TopicContextProvider
            dataMethod={getNotificationNamesWithPagination}
            itemId={itemId}
            topic={topic}
            selectedItem={selectedItem}
        >
            <ItemContentTabTwo>
                <SimpleItemDetails
                    breadcrumb={breadcrumb}
                    busy={busy}
                    getTabContent={getTabContent}
                    itemProfile={currentItem}
                    onBack={() => history.push(allItemsPath)}
                    saveDetails={updateNotification}
                    tabs={tabs}
                    initialValues={currentItem}
                >
                    <NotificationCard />
                </SimpleItemDetails>
            </ItemContentTabTwo>
        </TopicContextProvider>
    );
};

export default NotificationPage;
